import * as React from 'react'
import * as API from '../lib/api'
import { PurchaseOption } from '../lib/purchase_option'
import { TaxInformation } from '../lib/tax_information'
import { Testimonials } from '../Testimonials'
import { OrderDetails } from './OrderDetails'

// The redesigned right rail (CD-155): swaps its content to match the active step
// (testimonial / imagery / Order Details — see `renderContent`). Presentational;
// the host owns the step id and purchase-option/tax state.
interface IProps {
  currentStepId: string
  // The redesign's own social-proof set (CD-173), kept separate from the control
  // checkout's shared `testimonials` so its copy can be edited without touching
  // the A/B control arm.
  checkoutTestimonials: API.ITestimonial[]
  purchaseOptions: PurchaseOption[]
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  onPurchaseOptionChange: (name: API.PurchaseOptionNameType) => void
  taxInformation: TaxInformation
  trial: boolean
  productImagePath?: string
  // Defaults to decorative (alt=""); pass meaningful alt when the real asset
  // lands. Ignored while the placeholder is showing.
  productImageAlt?: string
}

// Steps that stack the full Order Details above the form on mobile, vs those
// that read as a top banner. The only hook the stylesheet needs; desktop renders
// both identically.
const STACKED_STEP_IDS = ['payment']

export class RightRail extends React.Component<IProps, object> {
  public render() {
    const { currentStepId } = this.props
    const stacked = STACKED_STEP_IDS.includes(currentStepId)
    const variant = stacked ? 'stacked' : 'banner'

    return (
      <div
        className={`trial-rail__content trial-rail__content--${variant}`}
        data-testid="trial-rail-content"
      >
        {this.renderContent()}
      </div>
    )
  }

  // An unrecognized step renders nothing rather than throwing, so a transient
  // step id leaves the rail empty instead of unmounting the form.
  protected renderContent() {
    switch (this.props.currentStepId) {
      case 'account':
        return this.renderTestimonial()
      case 'billing':
        return this.renderImagery()
      case 'payment':
        return this.renderOrderDetails()
      default:
        return null
    }
  }

  // The back end already samples one testimonial (CD-173), so `count={1}` is a
  // belt-and-suspenders cap, not a selector. Rotation is deferred, not missed.
  protected renderTestimonial() {
    return (
      <div className="trial-rail__testimonial" data-testid="rail-testimonial">
        <Testimonials testimonials={this.props.checkoutTestimonials} count={1} />
      </div>
    )
  }

  // A styled placeholder stands in until design delivers the asset, so the rail
  // keeps its footprint and there's no desktop layout shift.
  protected renderImagery() {
    const { productImagePath, productImageAlt } = this.props

    return (
      <div className="trial-rail__imagery" data-testid="rail-imagery">
        {productImagePath ? (
          <img
            className="trial-rail__imagery-asset"
            src={productImagePath}
            alt={productImageAlt ?? ''}
          />
        ) : (
          <div className="trial-rail__imagery-placeholder" aria-hidden="true" />
        )}
      </div>
    )
  }

  protected renderOrderDetails() {
    const {
      purchaseOptions,
      selectedPurchaseOptionName,
      onPurchaseOptionChange,
      taxInformation,
      trial,
    } = this.props

    return (
      <OrderDetails
        purchaseOptions={purchaseOptions}
        selectedPurchaseOptionName={selectedPurchaseOptionName}
        onPurchaseOptionChange={onPurchaseOptionChange}
        taxInformation={taxInformation}
        trial={trial}
      />
    )
  }
}
